import {useCallback, useMemo, useContext} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {selectBackgrounds} from '~/CreateFabricInvitation/reducers/fabricTemplate/selectors';
import {
  UPDATE_DESIGN_OPTIONS,
  RESET_DESIGN_OPTIONS,
} from '~/CreateFabricInvitation/reducers/fabricDesign/constants';
import {
  selectBackgroundId,
  selectPremiumFeaturesPristine,
  selectShouldTogglePremiumBackground,
  selectHasBackgroundChanged,
  selectInitBackgroundColor,
} from '~/CreateFabricInvitation/reducers/fabricDesign/selectors';
import {isAllowedVideoFormat} from '~/common/fabric/utils';
import {eventIsPremium} from '~/common/fabric/reducers/details/selectors';
import {EventCreateContext} from '~/CreateFabricInvitation/routes/EventCreateDesign/EventCreateContext';

export const useBackground = (isMobile = false) => {
  const dispatch = useDispatch();
  const backgrounds = useSelector(selectBackgrounds);
  const backgroundId = useSelector(selectBackgroundId);
  const initBackgroundColor = useSelector(selectInitBackgroundColor);
  const isPremium = useSelector(eventIsPremium);
  const premiumFeaturesPristine = useSelector(selectPremiumFeaturesPristine);
  const shouldShowPremiumBackground = useSelector(selectShouldTogglePremiumBackground);
  const {setBkgColor} = useContext(EventCreateContext);
  const hasBackgroundChanged = useSelector(selectHasBackgroundChanged);

  const onBackgroundReset = useCallback(() => {
    setBkgColor(initBackgroundColor || '#c0c0c0');
    dispatch({
      type: RESET_DESIGN_OPTIONS,
      payload: ['backgroundColor', 'backgroundId'],
    });
  }, [initBackgroundColor, setBkgColor]);

  const backgroundIcons = useMemo(
    () =>
      backgrounds
        ?.filter(({media}) => media.find(({label}) => label === 'desktop').url)
        ?.map(({media}) => media.find(({label}) => label === 'icon').url),
    [backgrounds]
  );

  const onColorChange = useCallback((background) => {
    dispatch({
      type: UPDATE_DESIGN_OPTIONS,
      payload: {
        backgroundColor: background,
        backgroundId: null,
        premiumFeaturesPristine: false,
        pristineBackground: false,
      },
    });
  }, []);

  const onTextureChange = useCallback(
    (icon) => {
      const selected = backgrounds?.find(
        ({media}) => media.find(({label}) => label === 'icon').url === icon
      );
      dispatch({
        type: UPDATE_DESIGN_OPTIONS,
        payload: {
          backgroundId: selected.id,
          backgroundColor: null,
          premiumFeaturesPristine: false,
          pristineBackground: false,
        },
      });
    },
    [backgrounds]
  );

  const selectedBackground = useMemo(() => {
    let selected = false;
    // Only allow default background images/animations for premium templates
    // or freemium coming from the gallery upsell
    if (isPremium || !premiumFeaturesPristine || shouldShowPremiumBackground)
      selected = backgrounds?.find(({id}) => id === backgroundId);
    if (selected) {
      const mode = isMobile ? 'mobile' : 'desktop';
      const src = selected.media.find(({label}) => label === mode).url;
      const icon = selected.media.find(({label}) => label === 'icon').url;
      const isVideo = isAllowedVideoFormat(src);

      return {...selected, src, icon, isVideo};
    }
    return undefined;
  }, [backgrounds, backgroundId, isPremium, premiumFeaturesPristine, shouldShowPremiumBackground]);

  return {
    backgroundIcons,
    onColorChange,
    onTextureChange,
    onBackgroundReset,
    selectedBackground,
    hasBackgroundChanged,
  };
};
